import React, { useState } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, RefreshCw, Info, Trash2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { useI18n } from "@/i18n";
import { levelDisplayName, levelWord, levelSystemOf } from "@/lib/levelLabels";
import { TermCalendarDialog, monthLabel, periodLabel } from "@/components/FeePlan";

export default function Academic() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  const mode = levelSystemOf(user);

  return (
    <div className="p-6 lg:p-8" data-testid="academic-page">
      <div className="mb-6">
        <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">{t("academic.title")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("academic.subtitle")}</p>
      </div>

      <LevelSystemCard mode={mode} />

      <Tabs defaultValue="grades">
        <TabsList>
          <TabsTrigger value="grades" data-testid="tab-grades">{mode === "age_band" ? t("academic.agesTab") : t("academic.gradesTab")}</TabsTrigger>
          <TabsTrigger value="sections" data-testid="tab-sections">{t("academic.sectionsTab")}</TabsTrigger>
          <TabsTrigger value="subjects" data-testid="tab-subjects">{t("academic.subjectsTab")}</TabsTrigger>
          <TabsTrigger value="years" data-testid="tab-years">{t("academic.yearsTab")}</TabsTrigger>
        </TabsList>
        <TabsContent value="grades"><GradesTab mode={mode} language={language} /></TabsContent>
        <TabsContent value="sections"><SectionsTab mode={mode} language={language} /></TabsContent>
        <TabsContent value="subjects"><SubjectsTab /></TabsContent>
        <TabsContent value="years"><YearsTab /></TabsContent>
      </Tabs>
    </div>
  );
}

function LevelSystemCard({ mode }) {
  const { t } = useI18n();
  const [mappingOpen, setMappingOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const { data: grades = [] } = useQuery({ queryKey: ["grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const qc = useQueryClient();
  const { refresh } = useAuth();

  const mut = useMutation({
    mutationFn: async (payload) => unwrap(await api.post("/academic/level-system", payload)),
    onSuccess: (data) => {
      setMappingOpen(false);
      setBackOpen(false);
      toast.success(t("academic.switchDone"));
      if (data.warnings?.length) {
        data.warnings.forEach((w) => toast.warning(w));
      }
      qc.invalidateQueries({ queryKey: ["grades"] });
      refresh();
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const confirm = (levelSystem, mappings) => mut.mutate({ level_system: levelSystem, mappings });

  return (
    <Card className="mb-6 border-[var(--subtle)]" data-testid="level-mode-card">
      <CardContent className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-[var(--text-muted)]">{t("academic.levelSystem")}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded font-semibold uppercase ${mode === "age_band" ? "bg-emerald-100 text-emerald-700" : "bg-[var(--accent)] text-white"}`}>
              {levelWord(mode, "en")}
            </span>
          </div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {mode === "age_band" ? t("academic.ageModeDesc") : t("academic.gradeModeDesc")}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {mode === "age_band" ? (
            <Button
              variant="outline"
              onClick={() => setBackOpen(true)}
              data-testid="switch-to-grades-btn"
            >
              {t("academic.switchToGrades")}
            </Button>
          ) : (
            <Button
              variant="outline"
              onClick={() => setMappingOpen(true)}
              data-testid="switch-level-mode-btn"
            >
              <RefreshCw className="h-4 w-4 mr-1.5" />
              {t("academic.switchToAges")}
            </Button>
          )}
        </div>
      </CardContent>

      <MappingDialog
        open={mappingOpen}
        onOpenChange={setMappingOpen}
        grades={grades}
        onConfirm={(mappings) => confirm("age_band", mappings)}
        pending={mut.isPending}
      />
      <SwitchBackDialog
        open={backOpen}
        onOpenChange={setBackOpen}
        onConfirm={() => confirm("grade", [])}
        pending={mut.isPending}
      />
    </Card>
  );
}

function SwitchBackDialog({ open, onOpenChange, onConfirm, pending }) {
  const { t } = useI18n();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{t("academic.switchToGradesTitle")}</DialogTitle>
          <DialogDescription>{t("academic.switchToGradesDesc")}</DialogDescription>
        </DialogHeader>
        <p className="text-sm text-[var(--text-muted)]">{t("academic.switchToGradesHint")}</p>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("common.cancel")}
          </Button>
          <Button
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            disabled={pending}
            onClick={() => onConfirm()}
            data-testid="confirm-switch-btn"
          >
            {pending ? t("academic.switching") : t("academic.confirmSwitch")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MappingDialog({ open, onOpenChange, grades, onConfirm, pending }) {
  const { t, language } = useI18n();
  const { data: bands = [] } = useQuery({ queryKey: ["age-bands"], queryFn: async () => unwrap(await api.get("/academic/age-bands")) });
  const [mapping, setMapping] = useState({});

  React.useEffect(() => {
    if (open) setMapping({});
  }, [open, grades]);

  const bandLabel = (band) => (language === "ar" && band.label_ar ? band.label_ar : band.label);

  const selectedCounts = {};
  Object.values(mapping).forEach((key) => {
    if (key) selectedCounts[key] = (selectedCounts[key] || 0) + 1;
  });
  const duplicateBands = Object.keys(selectedCounts).filter((key) => selectedCounts[key] > 1);
  const unmappedCount = grades.filter((g) => !mapping[g.id]).length;
  const allMapped = unmappedCount === 0 && grades.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {t("academic.switchToAgesTitle")}
          </DialogTitle>
          <DialogDescription>
            {t("academic.switchToAgesDesc")}
          </DialogDescription>
        </DialogHeader>

        {grades.length === 0 ? (
          <p className="text-sm text-[var(--text-muted)]">{t("academic.noLevelsYet")}</p>
        ) : (
          <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
            {grades.map((g) => (
              <div key={g.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 border border-[var(--subtle)] rounded-md p-3">
                <div className="flex-1 text-sm font-medium">{levelDisplayName(g, language, grades)}</div>
                <Select
                  value={mapping[g.id] || ""}
                  onValueChange={(v) => setMapping((prev) => ({ ...prev, [g.id]: v }))}
                >
                  <SelectTrigger className="w-full sm:w-64" data-testid={`wizard-band-${g.id}`}>
                    <SelectValue placeholder={t("academic.chooseBand")} />
                  </SelectTrigger>
                  <SelectContent>
                    {bands.map((b) => (
                      <SelectItem key={b.key} value={b.key}>{bandLabel(b)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
            {duplicateBands.length > 0 && (
              <div className="flex items-start gap-2 text-sm text-sky-800 bg-sky-50 border border-sky-200 rounded-md p-3">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{t("academic.sharedBandNote", { count: Math.max(...duplicateBands.map((key) => selectedCounts[key])) })}</span>
              </div>
            )}
            {unmappedCount > 0 && (
              <div className="text-sm text-[var(--text-muted)]">
                {t("academic.unmappedHint", { count: unmappedCount })}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={pending}>
            {t("common.cancel")}
          </Button>
          <Button
            className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            disabled={pending || !allMapped}
            onClick={() => onConfirm(grades.map((g) => ({ grade_id: g.id, age_band_key: mapping[g.id] || null })))}
            data-testid="confirm-switch-btn"
          >
            {pending ? t("academic.switching") : t("academic.confirmSwitch")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function GradesTab({ mode, language }) {
  const { t } = useI18n();
  const { data, isLoading } = useQuery({ queryKey: ["grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const { data: bands = [] } = useQuery({ queryKey: ["age-bands"], queryFn: async () => unwrap(await api.get("/academic/age-bands")) });
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", display_order: "", band_key: "" });

  const gradeMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/grades", { name: form.name, display_order: Number(form.display_order) })),
    onSuccess: () => { toast.success(t("academic.gradeAdded")); setForm({ name: "", display_order: "", band_key: "" }); qc.invalidateQueries({ queryKey: ["grades"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const bandMut = useMutation({
    mutationFn: async () => {
      const band = bands.find((b) => b.key === form.band_key);
      return unwrap(await api.post("/grades", {
        name: band.label,
        name_ar: band.label_ar || null,
        display_order: Number(form.display_order || 0),
        level_system: "age_band",
        age_min_days: band.min_days,
        age_max_days: band.max_days,
      }));
    },
    onSuccess: () => { toast.success(t("academic.bandAdded")); setForm({ name: "", display_order: "", band_key: "" }); qc.invalidateQueries({ queryKey: ["grades"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (gradeId) => unwrap(await api.delete(`/grades/${gradeId}`)),
    onSuccess: () => { toast.success(t("academic.levelDeleted")); qc.invalidateQueries({ queryKey: ["grades"] }); qc.invalidateQueries({ queryKey: ["sections"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const isAgeMode = mode === "age_band";
  const bandLabel = (band) => (language === "ar" && band.label_ar ? band.label_ar : band.label);

  return (
    <div className="mt-6 grid md:grid-cols-3 gap-6">
      <Card className="md:col-span-2 border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? <Skeleton className="h-64" /> : (
            <table className="w-full text-sm grid-table">
              <thead><tr><th className="text-left px-4 py-2.5">Order</th><th className="text-left px-4 py-2.5">{levelWord(mode, language)}</th><th className="w-12" /></tr></thead>
              <tbody>
                {(data || []).map((g) => (
                  <tr key={g.id} data-testid={`grade-${g.name}`}>
                    <td className="px-4 py-2.5 font-mono-num">{g.display_order}</td>
                    <td className="px-4 py-2.5 font-medium">{levelDisplayName(g, language, data)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => deleteMut.mutate(g.id)}
                        disabled={deleteMut.isPending}
                        aria-label={t("academic.deleteLevel")}
                        data-testid={`delete-grade-${g.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">{isAgeMode ? t("academic.addBand") : t("academic.addGrade")}</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isAgeMode ? (
            <>
              <Select value={form.band_key} onValueChange={(v) => setForm({ ...form, band_key: v })}>
                <SelectTrigger data-testid="age-band-select"><SelectValue placeholder={t("academic.chooseBand")} /></SelectTrigger>
                <SelectContent>
                  {bands.map((b) => <SelectItem key={b.key} value={b.key}>{bandLabel(b)}</SelectItem>)}
                </SelectContent>
              </Select>
              <Input placeholder={t("academic.displayOrder")} type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="age-band-order" />
              <Button onClick={() => bandMut.mutate()} disabled={!form.band_key || bandMut.isPending} data-testid="add-age-band" className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                <Plus className="h-4 w-4 mr-1" /> {t("common.save")}
              </Button>
            </>
          ) : (
            <>
              <Input placeholder={t("academic.namePlaceholder")} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="grade-name" />
              <Input placeholder={t("academic.displayOrder")} type="number" value={form.display_order} onChange={(e) => setForm({ ...form, display_order: e.target.value })} data-testid="grade-order" />
              <Button onClick={() => gradeMut.mutate()} disabled={!form.name || gradeMut.isPending} data-testid="add-grade" className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
                <Plus className="h-4 w-4 mr-1" /> {t("common.save")}
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SectionsTab({ mode, language }) {
  const { t } = useI18n();
  const { data: grades } = useQuery({ queryKey: ["grades"], queryFn: async () => unwrap(await api.get("/grades")) });
  const { data: years } = useQuery({ queryKey: ["ay"], queryFn: async () => unwrap(await api.get("/academic-years")) });
  const { data: sections, isLoading } = useQuery({ queryKey: ["sections"], queryFn: async () => unwrap(await api.get("/sections")) });
  const { data: staff } = useQuery({ queryKey: ["staff"], queryFn: async () => unwrap(await api.get("/staff")) });
  const qc = useQueryClient();
  const currentYear = (years || []).find((y) => y.is_current) || (years || [])[0];
  const [form, setForm] = useState({ grade_id: "", name: "", room: "", capacity: 40 });
  const [staffingForm, setStaffingForm] = useState({ section_id: "", class_teacher_user_id: "__none__", assistant_teacher_user_id: "__none__" });
  const teachers = (staff || []).filter((u) => (u.role_assignments || []).some((r) => r.role === "teacher"));
  const teacherById = Object.fromEntries(teachers.map((te) => [te.id, te]));

  const applySectionStaffing = (sectionId) => {
    const selected = (sections || []).find((s) => s.id === sectionId);
    setStaffingForm({
      section_id: sectionId,
      class_teacher_user_id: selected?.class_teacher_user_id || "__none__",
      assistant_teacher_user_id: selected?.assistant_teacher_user_id || "__none__",
    });
  };

  React.useEffect(() => {
    if (!staffingForm.section_id && (sections || []).length > 0) {
      applySectionStaffing(sections[0].id);
    }
  }, [sections, staffingForm.section_id]);

  React.useEffect(() => {
    if (!staffingForm.section_id) {
      return;
    }
    const selected = (sections || []).find((s) => s.id === staffingForm.section_id);
    if (!selected) {
      return;
    }
    setStaffingForm((prev) => ({
      ...prev,
      class_teacher_user_id: selected.class_teacher_user_id || "__none__",
      assistant_teacher_user_id: selected.assistant_teacher_user_id || "__none__",
    }));
  }, [sections, staffingForm.section_id]);

  const mut = useMutation({
    mutationFn: async () => unwrap(await api.post("/sections", { ...form, academic_year_id: currentYear?.id, medium: "english" })),
    onSuccess: () => { toast.success("Section added"); qc.invalidateQueries({ queryKey: ["sections"] }); setForm({ grade_id: "", name: "", room: "", capacity: 40 }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const staffingMut = useMutation({
    mutationFn: async () => {
      const payload = {
        class_teacher_user_id: staffingForm.class_teacher_user_id === "__none__" ? null : staffingForm.class_teacher_user_id,
        assistant_teacher_user_id: staffingForm.assistant_teacher_user_id === "__none__" ? null : staffingForm.assistant_teacher_user_id,
      };
      return unwrap(await api.patch(`/sections/${staffingForm.section_id}/staffing`, payload));
    },
    onSuccess: () => {
      toast.success("Section staffing updated");
      qc.invalidateQueries({ queryKey: ["sections"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  return (
    <div className="mt-6 grid md:grid-cols-3 gap-6">
      <Card className="md:col-span-2 border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? <Skeleton className="h-64" /> : (
            <table className="w-full text-sm grid-table">
              <thead><tr><th className="text-left px-4 py-2.5">{levelWord(mode, language)}</th><th className="text-left px-4 py-2.5">Section</th><th className="text-left px-4 py-2.5">Room</th><th className="text-left px-4 py-2.5">Capacity</th><th className="text-left px-4 py-2.5">Class teacher</th><th className="text-left px-4 py-2.5">Assistant</th></tr></thead>
              <tbody>
                {(sections || []).map((s) => {
                  const g = (grades || []).find((gr) => gr.id === s.grade_id);
                  const classTeacher = teacherById[s.class_teacher_user_id];
                  const assistantTeacher = teacherById[s.assistant_teacher_user_id];
                  return (
                    <tr key={s.id}>
                      <td className="px-4 py-2.5">{levelDisplayName(g, language, grades)}</td>
                      <td className="px-4 py-2.5 font-medium">{s.name}</td>
                      <td className="px-4 py-2.5 font-mono-num text-xs">{s.room}</td>
                      <td className="px-4 py-2.5 font-mono-num">{s.capacity}</td>
                      <td className="px-4 py-2.5 text-xs">{classTeacher?.full_name || "Unassigned"}</td>
                      <td className="px-4 py-2.5 text-xs">{assistantTeacher?.full_name || "Unassigned"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <div className="space-y-6">
        <Card className="border-[var(--subtle)]">
          <CardHeader><CardTitle className="text-sm font-display">Add section</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={form.grade_id} onValueChange={(v) => setForm({ ...form, grade_id: v })}>
              <SelectTrigger data-testid="section-grade"><SelectValue placeholder={levelWord(mode, language)} /></SelectTrigger>
              <SelectContent>{(grades || []).map((g) => <SelectItem key={g.id} value={g.id}>{levelDisplayName(g, language, grades)}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Name (A, B, C)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="section-name" />
            <Input placeholder="Room" value={form.room} onChange={(e) => setForm({ ...form, room: e.target.value })} />
            <Input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: Number(e.target.value) })} />
            <Button onClick={() => mut.mutate()} disabled={!form.grade_id || !form.name || mut.isPending} data-testid="add-section" className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </CardContent>
        </Card>

        <Card className="border-[var(--subtle)]">
          <CardHeader><CardTitle className="text-sm font-display">Assign staffing</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={staffingForm.section_id} onValueChange={applySectionStaffing}>
              <SelectTrigger data-testid="staffing-section"><SelectValue placeholder="Section" /></SelectTrigger>
              <SelectContent>
                {(sections || []).map((s) => {
                  const g = (grades || []).find((grade) => grade.id === s.grade_id);
                  return <SelectItem key={s.id} value={s.id}>{levelDisplayName(g, language)} · {s.name}</SelectItem>;
                })}
              </SelectContent>
            </Select>

            <Select value={staffingForm.class_teacher_user_id} onValueChange={(v) => setStaffingForm({ ...staffingForm, class_teacher_user_id: v })}>
              <SelectTrigger data-testid="staffing-class-teacher"><SelectValue placeholder="Class teacher" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teachers.map((te) => <SelectItem key={te.id} value={te.id}>{te.full_name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Select value={staffingForm.assistant_teacher_user_id} onValueChange={(v) => setStaffingForm({ ...staffingForm, assistant_teacher_user_id: v })}>
              <SelectTrigger data-testid="staffing-assistant-teacher"><SelectValue placeholder="Assistant teacher" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {teachers.map((te) => <SelectItem key={te.id} value={te.id}>{te.full_name}</SelectItem>)}
              </SelectContent>
            </Select>

            <Button
              onClick={() => staffingMut.mutate()}
              disabled={!staffingForm.section_id || staffingMut.isPending}
              data-testid="save-staffing"
              className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
            >
              Save staffing
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function SubjectsTab() {
  const { data, isLoading } = useQuery({ queryKey: ["subjects"], queryFn: async () => unwrap(await api.get("/subjects")) });
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", code: "" });
  const mut = useMutation({
    mutationFn: async () => unwrap(await api.post("/subjects", form)),
    onSuccess: () => { toast.success("Subject added"); qc.invalidateQueries({ queryKey: ["subjects"] }); setForm({ name: "", code: "" }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="mt-6 grid md:grid-cols-3 gap-6">
      <Card className="md:col-span-2 border-[var(--subtle)]">
        <CardContent className="p-0">
          {isLoading ? <Skeleton className="h-64" /> : (
            <table className="w-full text-sm grid-table">
              <thead><tr><th className="text-left px-4 py-2.5">Code</th><th className="text-left px-4 py-2.5">Name</th></tr></thead>
              <tbody>
                {(data || []).map((s) => (<tr key={s.id}><td className="px-4 py-2.5 font-mono-num">{s.code}</td><td className="px-4 py-2.5 font-medium">{s.name}</td></tr>))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">Add subject</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Input placeholder="Code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} />
          <Button onClick={() => mut.mutate()} disabled={!form.name || mut.isPending} className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function YearsTab() {
  const { t, language } = useI18n();
  const { user } = useAuth();
  // Mirrors the backend rule for the term calendar (management roles only).
  const canEditCalendar = (user?.roles || []).some((role) => ["platform_admin", "tenant_owner", "administrator", "principal"].includes(role));
  // The year stays set while the dialog animates closed.
  const [calendarYear, setCalendarYear] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const { data, isLoading } = useQuery({ queryKey: ["ay"], queryFn: async () => unwrap(await api.get("/academic-years")) });
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: "", start_date: "", end_date: "", is_current: true });
  const mut = useMutation({
    mutationFn: async () => unwrap(await api.post("/academic-years", form)),
    onSuccess: () => { toast.success("Academic year added"); setForm({ name: "", start_date: "", end_date: "", is_current: false }); qc.invalidateQueries({ queryKey: ["ay"] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  return (
    <div className="mt-6 grid md:grid-cols-3 gap-6">
      <Card className="border-[var(--subtle)] md:col-span-2 min-w-0">
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? <Skeleton className="h-32" /> : (
            <table className="w-full text-sm grid-table">
              <thead><tr><th className="text-left px-4 py-2.5">Year</th><th className="text-left px-4 py-2.5">Start</th><th className="text-left px-4 py-2.5">End</th><th className="text-left px-4 py-2.5">Status</th><th className="text-left px-4 py-2.5">{t("feePlan.termCalendar")}</th></tr></thead>
              <tbody>
                {(data || []).map((y) => (
                  <tr key={y.id}>
                    <td className="px-4 py-2.5 font-medium">{y.name} {y.is_current && <span className="ml-2 text-[10px] bg-[var(--accent)] text-white px-1.5 py-0.5 rounded font-semibold uppercase">Current</span>}</td>
                    <td className="px-4 py-2.5 font-mono-num text-xs">{y.start_date}</td>
                    <td className="px-4 py-2.5 font-mono-num text-xs">{y.end_date}</td>
                    <td className="px-4 py-2.5 capitalize">{y.status}</td>
                    <td className="px-4 py-2.5" data-testid={`year-calendar-${y.name}`}>
                      <div className="text-xs text-[var(--text-muted)]">
                        {(y.periods || []).length === 0 ? t("feePlan.calendarNotSet") : (y.periods || []).map((p) => (
                          <div key={p.key}>{periodLabel(p, language)}: {p.months.map((m) => monthLabel(m, language)).join(" ")}</div>
                        ))}
                      </div>
                      {canEditCalendar && (
                        <Button size="sm" variant="outline" className="mt-1 h-7" onClick={() => { setCalendarYear(y); setCalendarOpen(true); }} data-testid={`edit-calendar-${y.name}`}>{t("feePlan.termCalendar")}</Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
      <Card className="border-[var(--subtle)]">
        <CardHeader><CardTitle className="text-sm font-display">Add academic year</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <Input placeholder="Name (e.g. 2026-27)" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="academic-year-name" />
          <div><Label className="text-xs uppercase">Start date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="academic-year-start" /></div>
          <div><Label className="text-xs uppercase">End date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="academic-year-end" /></div>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.is_current} onChange={(e) => setForm({ ...form, is_current: e.target.checked })} /> Set as current year</label>
          <Button onClick={() => mut.mutate()} disabled={!form.name || !form.start_date || !form.end_date || mut.isPending} data-testid="add-academic-year" className="w-full bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"><Plus className="h-4 w-4 mr-1" /> Add year</Button>
        </CardContent>
      </Card>
      <TermCalendarDialog year={calendarYear} open={calendarOpen} onOpenChange={setCalendarOpen} />
    </div>
  );
}
